import { getAddress, isAddressEqual } from 'viem';
import type { Hex } from 'viem';

import {
    anchorLabel,
    DEFAULT_ANCHOR_CHAIN_ID,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import {
    confirmPasskey,
    confirmPasskeyRemoval,
    requestPasskeyAssociation,
    requestPasskeyRegistrationOptions,
    startTradingSession,
    verifyPasskeyRegistration,
} from '@/lib/api/tradingSession';
import type {
    PasskeyAssociationResponse,
    PasskeyCredential,
    PasskeyRegistrationOptionsResponse,
    RequestPasskeyAssociation,
    RequestPasskeyRegistrationOptions,
    SerializedRegistrationCredential,
    StartTradingSessionRequest,
    TradingSessionStartResponse,
    VerifyPasskeyRegistrationRequest,
} from '@/lib/api/tradingSession';

import { getPasskeyAssertionWithFriendlyError } from './passkeyAssertion';
import { writePasskeyOwnerHint } from './passkeyOwnerHint';
import {
    submitPasskeyAssociationTransaction,
    submitPasskeyRemovalTransaction,
} from './passkeyWalletTransactions';
import { assertCanonicalTradingSessionChallenge } from './protocol';
import { resolveTradingSessionIdentity } from './walletResolution';
import type { TradingSessionIdentity } from './walletResolution';
import { getPasskeyAssertion } from './webauthn';
import type {
    PublicKeyCredentialRequestOptionsJSON,
    SerializedAssertionCredential,
} from './webauthn';
import { createPasskey } from './webauthnRegistration';

/**
 * What the user is being asked for, in order. Every one of them is a prompt
 * the browser or the wallet shows on its own; the dialog narrates them so the
 * user knows which prompt to expect and why the button is still busy.
 */
export type PasskeyRegistrationPhase =
    | 'creating'
    | 'possession'
    | 'association'
    | 'mining'
    | 'confirming';

interface PasskeyRegistrationInput {
    owner: string;
    label?: string;
    /** Anchor whose authorization wallet the passkey is registered on. */
    anchorChainId?: AnchorChainId;
    onPhase?: (phase: PasskeyRegistrationPhase) => void;
}

interface PasskeyRegistrationDependencies {
    resolveIdentity: (
        owner: string,
        anchorChainId: AnchorChainId,
    ) => Promise<TradingSessionIdentity>;
    requestRegistrationOptions: (
        request: RequestPasskeyRegistrationOptions,
    ) => Promise<PasskeyRegistrationOptionsResponse>;
    createCredential: (
        options: PasskeyRegistrationOptionsResponse['options'],
        label?: string,
    ) => Promise<SerializedRegistrationCredential>;
    verifyRegistration: (
        request: VerifyPasskeyRegistrationRequest,
    ) => Promise<PasskeyCredential>;
    requestSessionOptions: (
        request: StartTradingSessionRequest,
    ) => Promise<TradingSessionStartResponse>;
    getAssertion: (
        options: PublicKeyCredentialRequestOptionsJSON,
    ) => Promise<SerializedAssertionCredential>;
    requestAssociation: (
        id: string,
        request: RequestPasskeyAssociation,
    ) => Promise<PasskeyAssociationResponse>;
    submitAssociation: (
        owner: string,
        association: PasskeyAssociationResponse,
        onMining?: () => void,
        anchorChainId?: AnchorChainId,
    ) => Promise<Hex>;
    confirm: (id: string) => Promise<PasskeyCredential>;
    writeOwnerHint: (owner: string) => void;
    /** Test seam for the confirm retry pacing. */
    sleep?: (ms: number) => Promise<void>;
}

interface PasskeyRemovalInput {
    owner: string;
    credential: PasskeyCredential;
    anchorChainId?: AnchorChainId;
}

interface PasskeyRemovalDependencies {
    resolveIdentity: (
        owner: string,
        anchorChainId: AnchorChainId,
    ) => Promise<TradingSessionIdentity>;
    submitRemoval: (
        owner: string,
        authorizationWallet: string,
        accountHash: string,
        anchorChainId?: AnchorChainId,
    ) => Promise<Hex>;
    confirmRemoval: (id: string) => Promise<PasskeyCredential>;
}

const defaultRegistrationDependencies: PasskeyRegistrationDependencies = {
    resolveIdentity: resolveTradingSessionIdentity,
    requestRegistrationOptions: requestPasskeyRegistrationOptions,
    createCredential: createPasskey,
    verifyRegistration: verifyPasskeyRegistration,
    requestSessionOptions: startTradingSession,
    getAssertion: getPasskeyAssertion,
    requestAssociation: requestPasskeyAssociation,
    submitAssociation: submitPasskeyAssociationTransaction,
    confirm: confirmPasskey,
    writeOwnerHint: writePasskeyOwnerHint,
};

const defaultRemovalDependencies: PasskeyRemovalDependencies = {
    resolveIdentity: resolveTradingSessionIdentity,
    submitRemoval: submitPasskeyRemovalTransaction,
    confirmRemoval: confirmPasskeyRemoval,
};

function requireOwner(ownerInput: string | undefined): `0x${string}` {
    if (!ownerInput) {
        throw new Error('Connect the owner wallet to manage passkeys.');
    }
    return getAddress(ownerInput);
}

function assertIdentityOwner(
    requestedOwner: `0x${string}`,
    identity: TradingSessionIdentity,
): void {
    if (!isAddressEqual(requestedOwner, identity.owner)) {
        throw new Error(
            'The resolved wallet identity does not match the connected owner.',
        );
    }
}

export async function completePasskeyRegistration(
    input: PasskeyRegistrationInput,
    dependencies: PasskeyRegistrationDependencies = defaultRegistrationDependencies,
): Promise<PasskeyCredential> {
    const owner = requireOwner(input.owner);
    const anchorChainId = input.anchorChainId ?? DEFAULT_ANCHOR_CHAIN_ID;
    const onPhase = input.onPhase ?? (() => {});

    const identity = await dependencies.resolveIdentity(owner, anchorChainId);
    assertIdentityOwner(owner, identity);

    const registration = await dependencies.requestRegistrationOptions({
        owner: identity.owner,
    });
    onPhase('creating');
    const response = await dependencies.createCredential(
        registration.options,
        input.label,
    );
    // no owner typed-data signature here any more: the live owner-signed trading
    // session this UI already requires proves the same thing to the server
    const pending = await dependencies.verifyRegistration({
        challengeId: registration.challengeId,
        response,
        ...(input.label ? { label: input.label } : {}),
    });
    if (pending.status !== 'pending') {
        throw new Error(
            'The server did not return a pending passkey registration.',
        );
    }

    const session = await dependencies.requestSessionOptions({
        method: 'passkey',
        owner: identity.owner,
        authorizationWallet: identity.authorizationWallet,
        tradingWallet: identity.tradingWallet,
        windowSeconds: 300,
        anchorChainId,
    });
    if (session.method !== 'passkey') {
        throw new Error('The server returned the wrong trading-session method.');
    }
    if (session.publicKey.challenge !== session.challenge) {
        throw new Error(
            'The server returned mismatched passkey challenge options.',
        );
    }
    await assertCanonicalTradingSessionChallenge(
        session.statement,
        session.challenge,
    );
    onPhase('possession');
    const possession = await getPasskeyAssertionWithFriendlyError(
        dependencies.getAssertion,
        {
            ...session.publicKey,
            allowCredentials: [{ id: response.rawId, type: 'public-key' }],
        },
    );
    const association = await dependencies.requestAssociation(pending.id, {
        proof: {
            method: 'passkey',
            statement: session.statement,
            credentialId: possession.rawId,
            authenticatorData: possession.response.authenticatorData,
            clientDataJson: possession.response.clientDataJSON,
            signature: possession.response.signature,
        },
    });
    if (association.intent !== 'link') {
        throw new Error(
            'Passkeys can only be added to an existing authorization wallet.',
        );
    }
    if (
        !isAddressEqual(
            getAddress(association.subject),
            identity.authorizationWallet,
        )
    ) {
        throw new Error(
            'The server returned a different authorization wallet.',
        );
    }
    if (
        association.accountHash.toLowerCase() !==
        pending.publicKeyHash.toLowerCase()
    ) {
        throw new Error('The server returned a different passkey account hash.');
    }

    onPhase('association');
    await dependencies.submitAssociation(
        identity.owner,
        association,
        () => onPhase('mining'),
        anchorChainId,
    );
    onPhase('confirming');
    const active = await confirmWithRetry(pending.id, dependencies);
    if (active.status !== 'active') {
        throw new Error(
            `The server did not confirm the passkey on ${anchorLabel(anchorChainId)}.`,
        );
    }
    // seed the sign-in owner hint: the very next passkey sign-in on this
    // browser would otherwise take the discovery path (an extra WebAuthn
    // prompt) to learn an owner we literally just associated.
    dependencies.writeOwnerHint(identity.owner);
    return active;
}

const CONFIRM_ATTEMPTS = 6;
const CONFIRM_RETRY_MS = 3_000;

/**
 * The receipt is in, but the server reads the anchor through its own RPC, which
 * can lag the wallet's by a few blocks; `passkeyNotConfirmed` right after the
 * transaction is that lag, not a failure. Ask again a few times before giving
 * up (this is exactly where the button used to spin, or fail, for nothing).
 */
async function confirmWithRetry(
    id: string,
    dependencies: PasskeyRegistrationDependencies,
): Promise<PasskeyCredential> {
    const sleep =
        dependencies.sleep ??
        (async (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
    for (let attempt = 1; ; attempt++) {
        try {
            return await dependencies.confirm(id);
        } catch (error) {
            const code =
                typeof error === 'object' && error !== null && 'body' in error
                    ? (error as { body?: { code?: string } }).body?.code
                    : undefined;
            if (code !== 'passkeyNotConfirmed' || attempt >= CONFIRM_ATTEMPTS) {
                throw error;
            }
            await sleep(CONFIRM_RETRY_MS);
        }
    }
}

export async function completePasskeyRemoval(
    input: PasskeyRemovalInput,
    dependencies: PasskeyRemovalDependencies = defaultRemovalDependencies,
): Promise<PasskeyCredential> {
    const owner = requireOwner(input.owner);
    const anchorChainId = input.anchorChainId ?? DEFAULT_ANCHOR_CHAIN_ID;
    const identity = await dependencies.resolveIdentity(owner, anchorChainId);
    assertIdentityOwner(owner, identity);

    await dependencies.submitRemoval(
        identity.owner,
        identity.authorizationWallet,
        input.credential.publicKeyHash,
        anchorChainId,
    );
    const removed = await dependencies.confirmRemoval(input.credential.id);
    if (removed.status !== 'removed') {
        throw new Error('The server did not confirm the passkey removal.');
    }
    return removed;
}
